import { View, Text, StyleSheet } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";

// ──────────────────────────────────────────────────────────────────────────
// SFROfraCalculatedValue
//
// This is a PDF component that displays the OFRA (Open Field Rates Application)
// calculation results in the exported report.
//
// It shows:
// - The container used (e.g., Peak Milk Can)
// - The fertilizer type and its density (e.g., Urea (1.32 g/mL))
// - The fertilizer rate applied (e.g., 120 kg/ha)
// - The application method (e.g., Point, Band, or Broadcast)
// - The calculated result (e.g., "Points per Container: 5.28")
//
// This helps farmers understand exactly what was calculated and can use this
// information to apply the right amount of fertilizer to their fields.
// ──────────────────────────────────────────────────────────────────────────
function SFROfraCalculatedValue({
  volumeContainer,
  fertilizerType,
  fertilizerRate,
  applicationMethod,
  resultLabel,
  result,
}) {
  const { t } = useTranslation();

  // Only show this section if the farmer provided a result
  // (i.e., they filled in the OFRA calculator and got a valid result)
  return (
    <>
      {result && (
        <>
          {/* Section title */}
          <Text style={styles.sectionTitle}>
            {t("ofra.resultTitle") || "OFRA Fertilizer Rate Calculated Value"}
          </Text>

          {/* Display each input the farmer used in the calculation */}
          <Text style={styles.recommendationItem}>
            <Text style={styles.nutrientLabel}>{t("ofra.volumeContainer") || "Volume Container:"} </Text>
            {volumeContainer}
          </Text>

          <Text style={styles.recommendationItem}>
            <Text style={styles.nutrientLabel}>{t("ofra.fertilizerType") || "Fertilizer Type:"} </Text>
            {fertilizerType}
          </Text>

          <Text style={styles.recommendationItem}>
            <Text style={styles.nutrientLabel}>{t("ofra.fertilizerRate") || "Fertilizer Rate:"} </Text>
            {fertilizerRate}
          </Text>

          <Text style={styles.recommendationItem}>
            <Text style={styles.nutrientLabel}>{t("ofra.applicationMethod") || "Application Method:"} </Text>
            {applicationMethod}
          </Text>

          {/* Display the calculated result in a highlighted box */}
          <View style={styles.cropResult}>
            <Text style={styles.cropResultText}>{`${resultLabel}: ${result}`}</Text>
          </View>
        </>
      )}
    </>
  );
}

export default SFROfraCalculatedValue;

// ──────────────────────────────────────────────────────────────────────────
// PDF Styling
// These styles control how the OFRA section looks when printed to PDF
// ──────────────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  // Section heading
  sectionTitle: {
    fontSize: 13,
    fontWeight: "bold",
    marginBottom: 8,
  },
  // Each line showing an input value
  recommendationItem: {
    fontSize: 10,
    color: "#374151",
    marginBottom: 4,
    lineHeight: 1.5,
  },
  // Label for each line (e.g., "Volume Container:")
  nutrientLabel: {
    fontWeight: "bold",
  },
  // Highlighted box showing the final result
  cropResult: {
    marginTop: 8,
    padding: 10,
    border: "1px solid #bbf7d0",
    borderRadius: 6,
    backgroundColor: "#f0fdf4",
  },
  // Text inside the result box
  cropResultText: {
    fontSize: 10,
    color: "#374151",
  },
});
